import { useEffect, useMemo, useRef, useState } from 'react';
import type { OrderShopDTO, ShopDTO, WbOrdersPageDTO } from '../api';
import { api } from '../api';
import { Empty, ErrorBox, GlobalCompanyFilter, Loading, PageHead } from '../components';

type DateRange = { from: string; to: string };

const WB_STATUS_LABELS: Record<string, string> = {
  waiting: '等待备货', sorted: '已分拣', sold: '已售出', canceled: '已取消',
  canceled_by_client: '买家取消', declined_by_client: '买家拒收', defect: '商品瑕疵',
  ready_for_pickup: '待揽收', postponed_delivery: '延迟配送', accepted_by_carrier: '承运商已接收',
  sent_to_carrier: '已交承运商', canceled_by_carrier: '承运商取消'
};

const SUPPLIER_STATUS_LABELS: Record<string, string> = {
  new: '新订单', confirm: '待备货', complete: '已备货', cancel: '已取消'
};

const NUMERIC_CURRENCIES: Record<number, string> = {
  36: 'AUD', 51: 'AMD', 156: 'CNY', 196: 'CYP', 203: 'CZK', 208: 'DKK',
  344: 'HKD', 356: 'INR', 360: 'IDR', 364: 'IRR', 376: 'ILS', 392: 'JPY',
  398: 'KZT', 410: 'KRW', 417: 'KGS', 418: 'LAK', 422: 'LBP', 434: 'LYD',
  440: 'LTL', 458: 'MYR', 498: 'MDL', 504: 'MAD', 512: 'OMR', 604: 'PEN',
  634: 'QAR', 643: 'RUB', 682: 'SAR', 702: 'SGD', 710: 'ZAR', 752: 'SEK',
  756: 'CHF', 764: 'THB', 784: 'AED', 788: 'TND', 826: 'GBP', 840: 'USD',
  860: 'UZS', 901: 'TWD', 933: 'BYN', 941: 'RSD', 944: 'AZN', 946: 'RON',
  949: 'TRY', 960: 'XDR', 972: 'TJS', 978: 'EUR', 980: 'UAH', 981: 'GEL',
  985: 'PLN', 986: 'BRL'
};

function dateInShanghai(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return values['year'] + '-' + values['month'] + '-' + values['day'];
}

function shiftDate(value: string, days: number): string {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day! + days));
  return date.toISOString().slice(0, 10);
}

function initialDateRange(): DateRange {
  const today = dateInShanghai();
  return { from: today, to: today };
}

function formatShanghaiDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).format(date);
}

function currencyLabel(code: number | null): string {
  if (code === null) return '未知币种';
  return NUMERIC_CURRENCIES[code] ?? '币种代码 ' + code;
}

function formatMinor(amountMinor: string | null | undefined): string {
  if (!amountMinor || !/^-?\d+$/.test(amountMinor)) return '—';
  try {
    const value = BigInt(amountMinor);
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    const whole = absolute / 100n;
    const fraction = String(absolute % 100n).padStart(2, '0');
    return (negative ? '-' : '') + new Intl.NumberFormat('zh-CN').format(whole) + '.' + fraction;
  } catch {
    return '—';
  }
}

function formatAmount(amountMinor: string | null | undefined, currencyCode: number | null): string {
  const amount = formatMinor(amountMinor);
  return amount === '—' ? amount : amount + ' ' + currencyLabel(currencyCode);
}

function statusLabel(status: string | null | undefined, labels: Record<string, string>): string {
  if (!status) return '未返回';
  return labels[status] ?? status;
}

function statusTone(status: string | null | undefined): string {
  if (status === 'sold' || status === 'complete') return 'success';
  if (status?.startsWith('canceled') || status === 'declined_by_client' || status === 'defect' || status === 'cancel') return 'danger';
  if (status === 'sent_to_carrier' || status === 'accepted_by_carrier' || status === 'ready_for_pickup') return 'blue';
  if (status === 'waiting' || status === 'sorted' || status === 'new' || status === 'confirm') return 'waiting';
  return 'muted';
}

function StatusPill({ status, labels }: { status: string | null; labels: Record<string, string> }) {
  return <span className={'pill pill-' + statusTone(status)}>{statusLabel(status, labels)}</span>;
}

function CurrencyTotals({ totals }: { totals: WbOrdersPageDTO['stats']['originalSales'] }) {
  if (!totals.length) return <span className="metric-empty">暂无已售金额</span>;
  return <div className="order-currency-totals">{totals.map((total) =>
    <span key={String(total.currencyCode)}>{formatAmount(total.amountMinor, total.currencyCode)}</span>
  )}</div>;
}

export function OrdersPage({ globalMode = false }: { globalMode?: boolean }) {
  const [range, setRange] = useState<DateRange>(initialDateRange);
  const [shopId, setShopId] = useState('');
  const [wbStatus, setWbStatus] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [page, setPage] = useState(1);
  const [shops, setShops] = useState<Array<(OrderShopDTO | ShopDTO) & { status?: string }>>([]);
  const [data, setData] = useState<WbOrdersPageDTO | null>(null);
  const [dataKey, setDataKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [shopError, setShopError] = useState<Error | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pollKey, setPollKey] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [shopsLoading, setShopsLoading] = useState(true);
  const loadedOnce = useRef(false);

  const queryKey = [range.from, range.to, shopId, wbStatus, globalMode ? companyId : '', String(page)].join('|');
  const selectedShop = shops.find((shop) => shop.id === shopId);
  const activeShopCount = shops.filter((shop) => !shop.status || shop.status === 'active').length;
  const visibleData = dataKey === queryKey ? data : null;
  const syncStates = visibleData?.syncStates ?? [];
  const syncErrors = syncStates.filter((state) => state.lastError);
  const latestSync = syncStates.map((state) => state.lastSuccessAt).filter((value): value is string => Boolean(value))
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
  const backfillDone = syncStates.filter((state) => state.backfillComplete).length;

  useEffect(() => {
    let active = true;
    setShopsLoading(true);
    (globalMode ? api.getOrderShops(companyId || undefined) : api.getShops()).then((result) => {
      if (active) setShops(result.items.filter((shop) => !('mode' in shop) || shop.mode === 'real'));
    }).catch((reason: unknown) => {
      if (active) setShopError(reason instanceof Error ? reason : new Error('店铺列表读取失败'));
    }).finally(() => {
      if (active) setShopsLoading(false);
    });
    return () => { active = false; };
  }, [globalMode, companyId]);

  useEffect(() => {
    const timer = setInterval(() => setPollKey((current) => current + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    const query = {
      from: range.from, to: range.to, page,
      ...(globalMode && companyId ? { companyId } : {}),
      ...(shopId ? { shopId } : {}),
      ...(wbStatus ? { wbStatus } : {})
    };
    setError(null);
    if (loadedOnce.current) setUpdating(true);
    else setLoading(true);
    api.getOrders(query).then((result) => {
      if (!active) return;
      setData(result);
      setDataKey(queryKey);
      loadedOnce.current = true;
    }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason : new Error('订单读取失败'));
    }).finally(() => {
      if (active) {
        setLoading(false);
        setUpdating(false);
      }
    });
    return () => { active = false; };
  }, [queryKey, pollKey, refreshKey]);

  function applyPreset(preset: 'today' | 'yesterday' | '7days' | '30days'): void {
    const today = dateInShanghai();
    if (preset === 'today') setRange({ from: today, to: today });
    if (preset === 'yesterday') {
      const yesterday = shiftDate(today, -1);
      setRange({ from: yesterday, to: yesterday });
    }
    if (preset === '7days') setRange({ from: shiftDate(today, -6), to: today });
    if (preset === '30days') setRange({ from: shiftDate(today, -29), to: today });
    setPage(1);
  }

  async function refreshOrders(): Promise<void> {
    if (manualBusy || (selectedShop && selectedShop.status !== 'active')) return;
    setManualBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.refreshOrders(shopId || undefined);
      setMessage(result.requested > 0
        ? '已将 ' + result.requested + ' 家店铺加入同步队列；同步请求会经过 WB Marketplace 共享限流器。'
        : '当前没有可同步的有效店铺。');
      setRefreshKey((current) => current + 1);
    } catch (reason) {
      setError(reason instanceof Error ? reason : new Error('手动刷新请求失败'));
    } finally {
      setManualBusy(false);
    }
  }

  const statusOptions = useMemo(() => Object.entries(WB_STATUS_LABELS), []);

  return <>
    <PageHead
      title="订单管理"
      description="WB FBS 订单只读看板 · 按下单时间筛选，金额仅统计已售订单侧金额"
      actions={!globalMode ? <>
        <span className="order-sync-frequency">每 30 分钟同步</span>
        <button className="button button-primary" disabled={manualBusy || shopsLoading || activeShopCount === 0 || (selectedShop !== undefined && selectedShop.status !== 'active')} onClick={() => void refreshOrders()}>
          {manualBusy ? '已提交…' : '立即刷新'}
        </button>
      </> : <span className="readonly-badge">全局只读</span>}
    />

    {message && <div className="order-message" role="status">{message}</div>}
    {shopError && <ErrorBox error={shopError} />}
    {error && <div className="order-error"><ErrorBox error={error} onRetry={() => setPollKey((current) => current + 1)} /></div>}

    <section className="card order-filter-card">
      <div className="order-quick-ranges" aria-label="快捷日期范围">
        <button className="button button-small" onClick={() => applyPreset('today')}>今天</button>
        <button className="button button-small" onClick={() => applyPreset('yesterday')}>昨天</button>
        <button className="button button-small" onClick={() => applyPreset('7days')}>近 7 天</button>
        <button className="button button-small" onClick={() => applyPreset('30days')}>近 30 天</button>
      </div>
      <div className="filters order-filters">
        {globalMode && <GlobalCompanyFilter value={companyId} onChange={(value) => { setCompanyId(value); setShopId(''); setPage(1); }} />}
        <label className="order-filter-field"><span>开始日期</span><input className="input" type="date" value={range.from} max={range.to} onChange={(event) => { setRange((current) => ({ ...current, from: event.target.value })); setPage(1); }} /></label>
        <label className="order-filter-field"><span>结束日期</span><input className="input" type="date" value={range.to} min={range.from} onChange={(event) => { setRange((current) => ({ ...current, to: event.target.value })); setPage(1); }} /></label>
        <label className="order-filter-field"><span>店铺</span><select className="select" value={shopId} onChange={(event) => { setShopId(event.target.value); setPage(1); }}>
          <option value="">全部可见店铺</option>
          {shops.map((shop) => <option value={shop.id} key={shop.id}>{globalMode && 'companyName' in shop && shop.companyName ? `${shop.companyName} · ` : ''}{shop.name}{shop.status && shop.status !== 'active' ? '（已停用）' : ''}</option>)}
        </select></label>
        <label className="order-filter-field"><span>WB 履约状态</span><select className="select" value={wbStatus} onChange={(event) => { setWbStatus(event.target.value); setPage(1); }}>
          <option value="">全部状态</option>
          {statusOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select></label>
      </div>
    </section>

    {visibleData && <section className="order-stats" aria-label="订单统计">
      <article className="card metric-card"><div className="metric-label">订单总数</div><div className="metric-value">{visibleData.stats.orderCount.toLocaleString('zh-CN')}</div><div className="metric-foot">当前筛选结果</div></article>
      <article className="card metric-card"><div className="metric-label">商品件数</div><div className="metric-value">{visibleData.stats.itemCount.toLocaleString('zh-CN')}</div><div className="metric-foot">按订单记录数计，一条订单算 1 件</div></article>
      <article className="card metric-card"><div className="metric-label">已售订单</div><div className="metric-value">{visibleData.stats.soldCount.toLocaleString('zh-CN')}</div><div className="metric-foot">仅 wbStatus=sold</div></article>
      <article className="card metric-card"><div className="metric-label">已售订单金额 · 原币</div><CurrencyTotals totals={visibleData.stats.originalSales} /><div className="metric-foot">WB 订单侧金额，按币种分组</div></article>
      <article className="card metric-card"><div className="metric-label">已售订单金额 · 卖家币种</div><CurrencyTotals totals={visibleData.stats.convertedSales} /><div className="metric-foot">WB 订单侧换算金额，按币种分组</div></article>
      <article className="card metric-card order-status-card"><div className="metric-label">WB 履约状态分布</div>
        {visibleData.stats.statusDistribution.length
          ? <div className="order-status-distribution">{visibleData.stats.statusDistribution.map((item: WbOrdersPageDTO['stats']['statusDistribution'][number]) =>
            <span key={item.status ?? 'unknown'}><StatusPill status={item.status} labels={WB_STATUS_LABELS} /><strong>{item.count.toLocaleString('zh-CN')}</strong></span>
          )}</div>
          : <div className="metric-foot">当前筛选范围暂无订单</div>}
      </article>
    </section>}

    {visibleData && <section className="card order-sync-card">
      <div className="order-sync-overview">
        <span>最近成功同步：{formatShanghaiDate(latestSync)}</span>
        <span>首轮回溯：{backfillDone}/{syncStates.length} 家店铺完成</span>
        <span>{syncErrors.length ? '同步异常：' + syncErrors.length + ' 家店铺' : '店铺同步正常'}</span>
        {updating && <span>正在更新列表…</span>}
      </div>
      {syncStates.length > 0 && <details className="order-sync-details">
        <summary>各店铺同步情况（{syncStates.length}）</summary>
        <div className="table-wrap"><table><thead><tr><th>店铺</th><th>状态</th><th>最近成功</th><th>首轮回溯</th><th>错误</th></tr></thead>
          <tbody>{syncStates.map((state) => <tr key={state.shopId}>
            <td>{globalMode && state.companyName ? <>{state.companyName}<div className="cell-sub">{state.shopName}</div></> : state.shopName}</td><td>{state.syncing ? '同步中' : state.lastError ? '需重试' : '待同步'}</td>
            <td>{formatShanghaiDate(state.lastSuccessAt)}</td><td>{state.backfillComplete ? '已完成' : '进行中'}</td>
            <td className="order-sync-error-text">{state.lastError ?? '—'}</td>
          </tr>)}</tbody>
        </table></div>
      </details>}
    </section>}

    <section className="card order-list-card">
      {(loading || updating) && !visibleData ? <Loading label="正在读取订单…" /> : visibleData ? visibleData.items.length
        ? <div className="table-wrap"><table className="orders-table">
          <thead><tr>
            <th>下单时间（中国时区）</th><th>店铺</th><th>WB 订单 ID</th><th>商品 / 颜色</th><th>SKU / 条码</th>
            <th>nmId / chrtId</th><th>仓库 / 办公室</th><th>订单金额（原币）</th><th>卖家币种金额</th><th>供应商状态</th><th>WB 履约状态</th>
            <th>供货单</th><th>跨境运单号</th><th>跨境类型 / 货品类型</th>
          </tr></thead>
          <tbody>{visibleData.items.map((order) => <OrderRow order={order} key={order.shopId + ':' + order.wbOrderId} />)}</tbody>
        </table></div>
        : <Empty title="当前筛选没有订单" description="订单同步后会按下单时间显示在这里。" />
        : <div className="order-empty-state"><Empty title="订单暂不可用" description={error ? '请检查服务状态后重试。' : '等待订单数据读取。'} /></div>}
      {visibleData && <div className="pagination"><span>第 {visibleData.page} / {visibleData.totalPages} 页 · 共 {visibleData.totalCount.toLocaleString('zh-CN')} 条</span>
        <div className="pagination-actions">
          <button className="button button-small" disabled={visibleData.page <= 1 || updating} onClick={() => setPage((current) => Math.max(1, current - 1))}>上一页</button>
          <button className="button button-small" disabled={visibleData.page >= visibleData.totalPages || updating} onClick={() => setPage((current) => current + 1)}>下一页</button>
        </div>
      </div>}
    </section>
  </>;
}

function OrderRow({ order }: { order: WbOrdersPageDTO['items'][number] }) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  useEffect(() => setThumbnailFailed(false), [order]);
  const productUrl = order.nmId && /^[1-9]\d*$/.test(order.nmId)
    ? `https://www.wildberries.ru/catalog/${order.nmId}/detail.aspx`
    : null;
  const productDetails = <>
    <span className="order-product-thumbnail">
      {order.thumbnailUrl && !thumbnailFailed
        ? <img src={order.thumbnailUrl} loading="lazy" referrerPolicy="no-referrer" alt="" onError={() => setThumbnailFailed(true)} />
        : <span>{productUrl ? 'WB' : '暂无图片'}</span>}
    </span>
    <span className="order-product-copy">
      <span className="cell-title">{order.article ?? '未返回货号'}</span>
      <span className="cell-sub">颜色：{order.colorCode ?? '—'}</span>
      {productUrl && <span className="order-buyer-link-label">在 WB 查看买家页</span>}
    </span>
  </>;
  return <tr>
    <td className="order-time-cell">{formatShanghaiDate(order.createdAt)}</td>
    <td>{order.shopName}{order.companyName && <div className="cell-sub">{order.companyName}</div>}</td>
    <td className="mono">{order.wbOrderId}<div className="cell-sub">{order.orderUid ?? order.rid ?? '—'}</div></td>
    <td className="order-product-cell">{productUrl
      ? <a className="order-product-link" href={productUrl} target="_blank" rel="noopener noreferrer" aria-label={`打开商品 ${order.article ?? order.nmId} 的 WB 买家页`}>
        {productDetails}
      </a>
      : <div className="order-product-link order-product-unlinked">{productDetails}</div>}
    </td>
    <td className="order-sku-cell" title={order.skus.join(', ')}>{order.skus.length ? order.skus.join(', ') : '—'}</td>
    <td className="mono">{order.nmId ?? '—'}<div className="cell-sub">{order.chrtId ?? '—'}</div></td>
    <td className="mono">{order.warehouseId ?? '—'}<div className="cell-sub">{order.officeId ?? '—'}</div></td>
    <td>{formatAmount(order.priceMinor, order.currencyCode)}</td>
    <td>{formatAmount(order.convertedPriceMinor, order.convertedCurrencyCode)}</td>
    <td><StatusPill status={order.supplierStatus} labels={SUPPLIER_STATUS_LABELS} /></td>
    <td><StatusPill status={order.wbStatus} labels={WB_STATUS_LABELS} /></td>
    <td className="mono">{order.supplyId ?? '—'}</td>
    <td className="mono">{order.trackNumber ?? '—'}</td>
    <td>跨境 {order.crossBorderType ?? '—'}<div className="cell-sub">货品 {order.cargoType ?? '—'}</div></td>
  </tr>;
}
